import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrders } from "../store/features/ordersSlice";
import { Button, EmptyState, Loader, PageHeader } from "../components/ui";
import { formatZl, plural, PRODUCT_WORDS } from "../utils/format";
import {
  ShoppingBagIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

const STATUS = {
  new: { label: "Новый", className: "text-blue-300 bg-blue-500/15 border-blue-500/30", icon: ClockIcon },
  in_progress: {
    label: "В процессе",
    className: "text-amber-300 bg-amber-500/15 border-amber-500/30",
    icon: ClockIcon,
  },
  completed: {
    label: "Выполнен",
    className: "text-green-300 bg-green-500/15 border-green-500/30",
    icon: CheckCircleIcon,
  },
  cancelled: { label: "Отменен", className: "text-red-300 bg-red-500/15 border-red-500/30", icon: XCircleIcon },
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });

const OrderCard = ({ order }) => {
  const status = STATUS[order.status] || {
    label: order.status,
    className: "text-slate-300 bg-slate-500/15 border-slate-500/30",
    icon: ClockIcon,
  };
  const StatusIcon = status.icon;
  const count = order.items.reduce((n, item) => n + item.quantity, 0);

  return (
    <div className="bg-slate-900/80 border border-slate-700 rounded-2xl overflow-hidden">
      <div className="p-4 flex justify-between items-start border-b border-slate-800">
        <div>
          <div className="text-sm text-slate-400 mb-1">Заказ #{order.id}</div>
          <div className="font-bold">{formatDate(order.created_at)}</div>
        </div>
        <div
          className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold ${status.className}`}
        >
          <StatusIcon className="w-3.5 h-3.5" />
          {status.label}
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="flex -space-x-3 overflow-hidden">
          {order.items.slice(0, 4).map((item, idx) => (
            <div
              key={idx}
              className="w-12 h-12 rounded-xl border-2 border-slate-900 bg-white overflow-hidden"
            >
              <img
                src={item.product_image || "/logo.svg"}
                alt={item.product_name}
                className="w-full h-full object-contain"
              />
            </div>
          ))}
          {order.items.length > 4 && (
            <div className="w-12 h-12 rounded-xl border-2 border-slate-900 bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-300">
              +{order.items.length - 4}
            </div>
          )}
        </div>

        <div className="flex justify-between items-end gap-3">
          <div className="text-xs text-slate-400">
            {count} {plural(count, PRODUCT_WORDS)} • {order.payment_method_name || "Оплата при получении"}
            {Number(order.discount_amount) > 0 && (
              <span className="block text-emerald-400 mt-0.5">
                Скидка −{formatZl(order.discount_amount)}
              </span>
            )}
          </div>
          <div className="text-lg font-black text-blue-400 whitespace-nowrap">
            {formatZl(order.total_price)}
          </div>
        </div>
      </div>
    </div>
  );
};

const OrdersPage = () => {
  const dispatch = useDispatch();
  const { list: orders, status } = useSelector((state) => state.orders);

  useEffect(() => {
    dispatch(fetchOrders());
  }, [dispatch]);

  if (status === "loading" && orders.length === 0) {
    return <Loader className="min-h-[60vh]" />;
  }

  return (
    <div className="text-white">
      <PageHeader title="История заказов" />

      <main className="px-2 pb-6 max-w-xl mx-auto">
        {orders.length > 0 ? (
          <div className="space-y-4">
            {orders.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={ShoppingBagIcon}
            title="История пуста"
            text="Вы еще не сделали ни одного заказа. Самое время это исправить!"
            action={
              <Button to="/" size="lg" block className="max-w-xs">
                К товарам
              </Button>
            }
          />
        )}
      </main>
    </div>
  );
};

export default OrdersPage;
